import { z } from "zod";

/**
 * Response envelopes. The source system returns HTTP 200 with a body `code` for
 * everything, including business failures (`04-api-surface.md`) — that defeats
 * every tool that reads a status line, so we use real statuses and a small
 * RFC-7807-shaped error body.
 */

export const ErrorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string(),
    /**
     * The translatable reason, where the throw named one. A plain string
     * rather than the `ErrorReason` union: a client older than the API must
     * read the envelope it is sent and decide for itself that it does not know
     * the word, which is what `isErrorReason` is for.
     */
    reason: z.string().optional(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});

export type ErrorEnvelope = z.infer<typeof ErrorEnvelopeSchema>;

export const PageSchema = z.object({
  total: z.number().int().nonnegative(),
  size: z.number().int().positive(),
  current: z.number().int().positive(),
  pages: z.number().int().nonnegative(),
});

export type Page = z.infer<typeof PageSchema>;

/** `{ data, page }` — the list shape every collection endpoint returns. */
export function listEnvelope<T extends z.ZodType>(item: T) {
  return z.object({ data: z.array(item), page: PageSchema });
}

export const ListQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  size: z.coerce.number().int().positive().max(200).default(10),
  q: z.string().trim().optional(),
  sort: z.string().trim().optional(),
});

export type ListQuery = z.infer<typeof ListQuerySchema>;

export function pageOf(total: number, size: number, current: number): Page {
  return { total, size, current, pages: size > 0 ? Math.ceil(total / size) : 0 };
}

/** `PATCH /store/<resource>/sort` — the product-wide drag-to-sort contract. */
export const SortRequestSchema = z.object({
  items: z.array(z.object({ id: z.uuid(), sort: z.number().int() })).min(1),
});

export type SortRequest = z.infer<typeof SortRequestSchema>;
